import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { COMPETITION_TYPE_LABELS_UPPER as TYPE_LABELS, formatDateAbbreviatedFr } from '@shared';
import type { Competition } from '@shared/types/entities';
import { ArrowRight, Calendar, MapPin, Trophy, Users } from 'lucide-react';

const CARD_GRADIENTS = [
  'from-purple-500 to-indigo-500',
  'from-blue-500 to-indigo-500',
  'from-orange-500 to-red-500',
  'from-emerald-500 to-teal-500',
  'from-cyan-500 to-blue-500',
  'from-amber-500 to-orange-500',
];

export function UpcomingCard({ comp }: { comp: Competition }) {
  const gradient = CARD_GRADIENTS[comp.id % CARD_GRADIENTS.length];
  const placesLabel = comp.maxTeams
    ? `${comp.currentTeams}/${comp.maxTeams} équipes`
    : `${comp.currentTeams} inscrites`;

  return (
    <div className='bg-slate-50 rounded-2xl p-5 hover:bg-slate-100 transition-colors'>
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div className='flex items-center gap-4'>
          <div
            className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shrink-0`}
          >
            <Trophy className='w-6 h-6 text-white' />
          </div>
          <div>
            <h4 className='font-bold text-slate-900'>{comp.name}</h4>
            <div className='flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1'>
              <span className='flex items-center gap-1'>
                <Calendar className='w-3.5 h-3.5' />
                {comp.date ? formatDateAbbreviatedFr(comp.date) : '—'}
              </span>
              {(comp.venue ?? comp.city) && (
                <span className='flex items-center gap-1'>
                  <MapPin className='w-3.5 h-3.5' />
                  {comp.venue ?? comp.city}
                </span>
              )}
              <span className='px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-md'>
                {comp.type ? (TYPE_LABELS[comp.type] ?? comp.type) : '—'}
              </span>
            </div>
          </div>
        </div>
        <div className='flex items-center gap-4'>
          <div className='text-right shrink-0'>
            <div className='text-sm text-slate-500'>{placesLabel}</div>
            <div className='font-bold text-purple-600'>
              {comp.price ? `${(comp.price / 100).toFixed(0)} €` : 'Gratuit'}
            </div>
          </div>
          <Link
            to={ROUTES.CONCOUR.replace(':id', String(comp.id))}
            className='px-4 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition-colors flex items-center gap-1.5 shrink-0'
          >
            Détails <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </div>
    </div>
  );
}

export function PastCard({ comp }: { comp: Competition }) {
  return (
    <div className='bg-slate-50 rounded-2xl p-5'>
      <h4 className='font-bold text-slate-900'>{comp.name}</h4>
      <p className='text-sm text-slate-500 mt-1'>{comp.date ? formatDateAbbreviatedFr(comp.date) : '—'}</p>
      <div className='flex items-center gap-4 mt-3'>
        <span className='text-sm text-slate-600 flex items-center gap-1'>
          <Users className='w-4 h-4 text-blue-500' />
          {comp.currentTeams} équipe{comp.currentTeams > 1 ? 's' : ''}
        </span>
        <span className='px-2 py-0.5 bg-slate-200 text-slate-600 text-xs font-bold rounded-md'>
          {comp.type ? (TYPE_LABELS[comp.type] ?? comp.type) : '—'}
        </span>
      </div>
    </div>
  );
}

export function Skeleton() {
  return (
    <div className='bg-[#F8FAFC]'>
      <div className='h-80 bg-gradient-to-br from-purple-900 via-indigo-600 to-blue-800' />
      <section className='px-6'>
        <div className='max-w-7xl mx-auto -mt-24 relative z-10'>
          <div className='bg-white rounded-3xl border border-slate-200 shadow-xl p-8 animate-pulse'>
            <div className='flex gap-8'>
              <div className='w-32 h-32 bg-slate-200 rounded-3xl shrink-0' />
              <div className='flex-1 space-y-3 pt-2'>
                <div className='h-8 bg-slate-200 rounded w-1/3' />
                <div className='h-4 bg-slate-200 rounded w-1/2' />
                <div className='h-4 bg-slate-200 rounded w-1/4' />
              </div>
            </div>
            <div className='grid grid-cols-4 gap-4 mt-8'>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className='h-24 bg-slate-100 rounded-2xl' />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
